import {
  ApiChartTemplate,
  ApiChartType,
  ApiConversion,
  ApiConversionType,
  ApiDimension,
  ApiLocation,
  ApiMetric,
  ApiProjectDefault,
  ApiReservedConversion,
  ApiYAxisChartType,
} from "@incendium/api";
import { Delete, QuestionMark, Settings } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { locationService } from "Apis";
import GlassCard from "Components/GlassCard/GlassCard";
import { LocationTabs } from "Components/TagPages";
import SpacedList, { SpacedLinkButton } from "Components/UI/SpacedList";
import { StyledDrawerContainer } from "Components/UI/StyledDrawer";
import { sortAlpha } from "Helpers/arrays";
import { friendlyDateTime } from "Helpers/dates";
import { formatEnumVal } from "Helpers/enumToText";
import withInfoColumn from "HoC/withInfoColumn";
import { useSelectedProject } from "Hooks";
import { useConversions } from "Hooks/useConversions";
import { useLocations } from "Hooks/useLocations";
import { useConfirmation } from "Hooks/useConfirmation";
import useProjectTimezone from "Hooks/useProjectTimezone";
import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnalyticsChartStatCard } from "features/analytics";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";
import { ConversionSettingsDrawer } from "features/conversions/components";

function ConversionsPageMain() {
  const { selectedClient } = useSelectedProject();
  const { conversions, setConversions, refetch } = useConversions();
  const { locations } = useLocations();
  const handleDelete = useConfirmation();
  const history = useHistory();
  const timezone = useProjectTimezone();
  const [activeConversionId, setActiveConversionId] = useState<number | null>(
    null,
  );

  const activeConversion = useMemo(() => {
    if (activeConversionId == null) {
      return null;
    }
    return (
      conversions.find(
        (conv) => typeof conv.id === "number" && conv.id === activeConversionId,
      ) || null
    );
  }, [conversions, activeConversionId]);

  useEffect(() => {
    if (activeConversionId != null && !activeConversion) {
      setActiveConversionId(null);
    }
  }, [activeConversion, activeConversionId]);

  const orderedConversions = useMemo(() => {
    return sortAlpha(conversions, "name");
  }, [conversions]);

  const onDelete = useCallback(
    (conversion: ApiConversion) => {
      if (!conversion.id) {
        return;
      }
      handleDelete({
        title: `Are you sure you want to delete this conversion`,
        body: `This action can not be undone`,
        callback: async () => {
          await locationService.locationServiceDeleteConversion({
            locationId: 0, // todo, check this is ok. location id no longer part of conversion
            projectId: conversion.projectId as number,
            clientId: selectedClient?.id as number,
            conversionId: conversion.id as number,
          });
          setConversions(
            produce(conversions, (draft) => {
              const idx = draft.findIndex((d) => d.id === conversion.id);
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            }),
          );
          return "Conversion deleted";
        },
      });
    },
    [selectedClient, setConversions, conversions, handleDelete],
  );

  const onEdit = useCallback(
    (conversion: ApiConversion, location: ApiLocation) => {
      history.push(
        `../train/locations/${location.id}/location-tagging?tab=${
          "Conversion Tab" as LocationTabs
        }`,
        conversion,
      );
    },
    [history],
  );

  const handleConversionUpdated = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <>
      <SpacedList
        title="Conversions List"
        height={`calc(100vh - ${TOP_BAR_HEIGHT}px - 170px)`}
      >
        <TableHead>
          <TableRow>
            <TableCell>Conversion Name</TableCell>
            <TableCell>Locations</TableCell>
            <TableCell>Conversion Type</TableCell>
            <TableCell>Last Fired</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(orderedConversions || []).map((conversion) => {
            return (
              <TableRow key={conversion.id}>
                <TableCell>
                  <SpacedLinkButton
                    onClick={() => {
                      if (typeof conversion.id === "number") {
                        setActiveConversionId(conversion.id);
                      }
                    }}
                  >
                    {conversion.name || ""}
                  </SpacedLinkButton>
                  {conversion.reserved !==
                    ApiReservedConversion.NOT_RESERVED && (
                    <Typography variant="body2">
                      (Reserved : {formatEnumVal(conversion.reserved || "")})
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    {(conversion.locations || []).length > 0 ? (
                      (conversion.locations || []).map((location) => (
                        <Typography
                          variant="body2"
                          noWrap
                          key={location?.id ?? `location-${conversion.id}`}
                          color="text.primary"
                        >
                          {location?.name || "Unnamed location"}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No locations
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell width={200}>
                  <Typography variant="body1" noWrap>
                    {conversion.type === ApiConversionType.MACRO
                      ? "Conversion"
                      : "Interaction"}
                  </Typography>
                </TableCell>

                <TableCell width={250}>
                  <Typography variant="body1" noWrap>
                    Last Fired :
                    {conversion.lastFiredAt
                      ? friendlyDateTime(conversion.lastFiredAt, timezone)
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Manage conversion settings" arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            if (typeof conversion.id === "number") {
                              setActiveConversionId(conversion.id);
                            }
                          }}
                        >
                          <Settings />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {conversion._default !== ApiProjectDefault.UNSPECIFIED ? (
                      <Tooltip
                        arrow
                        title="This is a system default conversion that cannot be deleted"
                      >
                        <div>
                          <IconButton disabled size="small" color="secondary">
                            <Delete />
                          </IconButton>
                        </div>
                      </Tooltip>
                    ) : (
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => onDelete(conversion)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </SpacedList>

      <ConversionSettingsDrawer
        open={Boolean(activeConversion)}
        conversion={activeConversion}
        locations={locations}
        clientId={selectedClient?.id}
        onClose={() => setActiveConversionId(null)}
        onConversionUpdated={handleConversionUpdated}
        onOpenLocation={onEdit}
      />
    </>
  );
}

const ConversionsPageWithInfo = withInfoColumn(ConversionsPageMain);

function ConversionsPage() {
  return (
    <ConversionsPageWithInfo
      infoColumn={
        <>
          <AccordianChartBuilderSidebarBlock
            title="Conversions Help"
            subTitle="Conversions track important events that happen on your site."
            icon={QuestionMark}
            defaultClosed
          >
            <GlassCard boxProps={{ p: 2 }} noShadow>
              <Typography variant="body1">
                We have 2 types of conversions
              </Typography>
              <ul>
                <li>
                  Conversions - These should be used for your main goals such as
                  a booking, sale, an enquiry submission etc.
                </li>
                <li>
                  Interactions - These should be used for smaller but still
                  notable events. For example, someone interacting with an image
                  gallery on a product page, or clicking a 'Read More' link on a
                  blog category page.
                </li>
              </ul>
              <Typography variant="body1">
                A conversion can be used on more than one location. Set it up
                once, then add it to whichever locations need it—so you don’t
                have to configure the same conversion for every page or URL.
              </Typography>
              <ul>
                <li>
                  Click the conversion name or the settings icon to open it. You
                  can edit the name, type and reserved tag, and add or remove
                  which locations use this conversion.
                </li>
                <li>
                  Use the link next to each location to open that location’s
                  setup.
                </li>
                <li>Click the delete icon to remove the conversion.</li>
              </ul>
            </GlassCard>
          </AccordianChartBuilderSidebarBlock>

          <StyledDrawerContainer>
            <Stack spacing={2}>
              <AnalyticsChartStatCard
                topDimension={ApiDimension.DIMENSION_MACRO_CONVERSION}
                chartOutput={{
                  lastNDays: 90,
                  from: undefined,
                  to: undefined,
                }}
                forceComparison
                chart={{
                  name: "Top Fired Conversion",
                  dimension: [ApiDimension.DIMENSION_MACRO_CONVERSION],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.STAT_CARD,
                  comparison: true,
                  attributes: [],
                  displayOptions: {
                    statTitle: "Top Fired Conversion (last 90 days)",
                  },
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                    },
                  ],
                }}
                noToolbar
              />
              <AnalyticsChartStatCard
                topDimension={ApiDimension.DIMENSION_MICRO_CONVERSION}
                chartOutput={{
                  lastNDays: 90,
                  from: undefined,
                  to: undefined,
                }}
                chart={{
                  name: "Top Fired Interacion",
                  dimension: [ApiDimension.DIMENSION_MICRO_CONVERSION],
                  template: ApiChartTemplate.SNAPSHOT,
                  type: ApiChartType.STAT_CARD,
                  comparison: true,
                  attributes: [],
                  displayOptions: {
                    statTitle: "Top Fired Interacion (last 90 days)",
                  },
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT],
                    },
                  ],
                }}
                noToolbar
              />
              <AnalyticsCard
                height={380}
                overrideDate={{
                  lastNDays: 90,
                  from: undefined,
                  to: undefined,
                }}
                chart={{
                  name: "Conversions by Day (last 90 days)",
                  dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  attributes: [],
                  displayOptions: {
                    noLegend: true,
                  },
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                      chart: {
                        [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                          ApiYAxisChartType.BAR,
                      },
                    },
                  ],
                }}
                noToolbar
              />
            </Stack>
          </StyledDrawerContainer>
        </>
      }
    />
  );
}

export default ConversionsPage;
